import { useEffect, useState } from "react";
import apiClient from "../../../utils/api";
import AddIcon from "@mui/icons-material/Add";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  GridSlotProps,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import React from "react";

type WhitelistEntry = {
  id: string;
  email: string;
  status: string;
  isNew?: boolean;
};

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel
    ) => void;
  }
}

const EditToolBar = (props: GridSlotProps["toolbar"]) => {
  const { setRows, setRowModesModel } = props;

  const handleClick = () => {
    const id = uuidv4();

    setRows((oldRows) => [
      ...oldRows,
      { id, email: "example@kiwiai.org", status: "enabled", isNew: true },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "email" },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        Add record
      </Button>
    </GridToolbarContainer>
  );
};

export const Whitelist: React.FC = () => {
  const [whitelist, setWhitelist] = useState<WhitelistEntry[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const addUserToWhitelist = async (email: string) => {
    const response = await apiClient.whitelistControllerAddUserToWhitelist({
      addWhitelistRequest: {
        email: email,
      },
    });
    console.log(response);
  };

  const updateUserWhitelistStatus = async (
    email: string,
    status: "enabled" | "disabled"
  ) => {
    const response = await apiClient.whitelistControllerUpdateUserWhitelist({
      updateWhitelistRequest: {
        email: email,
        status: status,
      },
    });
    console.log(response);
  };

  useEffect(() => {
    const fetchWhitelist = async () => {
      const response = await apiClient.whitelistControllerListWhitelistUsers();
      console.log(response);
      const entriesList = response.entries.map((entry) => ({
        id: uuidv4(),
        email: entry.email,
        status: entry.status,
      }));
      setWhitelist(entriesList);
    };
    fetchWhitelist();
  }, []);

  const FormDialog = ({
    props: { open, setOpen },
  }: {
    props: {
      open: boolean;
      setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    };
  }) => {
    const handleClickOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.currentTarget);
      const formJson = Object.fromEntries((formData as any).entries());
      const emails = formJson.email
        .split(",")
        .map((email: string) => email.trim());

      emails.forEach((email: string) => {
        const updatedRow = {
          id: uuidv4(),
          email,
          status: "enabled",
          isNew: false,
        };
        setWhitelist((oldWhitelist) => [...oldWhitelist, updatedRow]);
        try {
          addUserToWhitelist(updatedRow.email);
        } catch (error) {
          console.error("Error adding user to whitelist:", error);
        }
      });

      handleClose();
    };

    return (
      <React.Fragment>
        <Button variant="outlined" onClick={handleClickOpen}>
          Open form dialog
        </Button>
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            component: "form",
            onSubmit: handleSubmit,
          }}
        >
          <DialogTitle>Add Multiple Emails to Whitelist</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please enter the email addresses here, separated by commas.
            </DialogContentText>
            <TextField
              autoFocus
              required
              margin="dense"
              id="name"
              name="email"
              label="Email Addresses"
              fullWidth
              variant="standard"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Subscribe</Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  };

  const AddMultiple = () => {
    const [open, setOpen] = useState(false);

    const handleClick = () => {
      setOpen(true);
    };

    return (
      <div>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          Add Multiple Records
        </Button>
        <FormDialog props={{ open, setOpen }} />
      </div>
    );
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.Edit },
    });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = whitelist.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setWhitelist(whitelist.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    console.log("newRow", newRow);
    const updatedRow = newRow as WhitelistEntry;
    // update server
    if (updatedRow.isNew === true) {
      try {
        addUserToWhitelist(updatedRow.email);
        updatedRow.isNew = false;
      } catch (error) {
        console.error("Error adding user to whitelist:", error);
      }
    } else {
      updateUserWhitelistStatus(
        updatedRow.email,
        updatedRow.status as "enabled" | "disabled"
      );
    }

    setWhitelist(
      whitelist.map((row) => (row.email === newRow.email ? updatedRow : row))
    );

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef<WhitelistEntry>[] = [
    { field: "email", editable: true, headerName: "Email", minWidth: 200 },
    {
      field: "status",
      editable: true,
      type: "singleSelect",
      valueOptions: ["enabled", "disabled"],
      headerName: "Status",
      minWidth: 200,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <div>
      <h1>Whitelist</h1>
      <AddMultiple />
      <DataGrid
        rows={whitelist}
        columns={columns}
        editMode="row"
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5]}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{ toolbar: EditToolBar }}
        slotProps={{
          toolbar: {
            setRows: (newRows) =>
              setWhitelist(newRows as unknown as WhitelistEntry[]),
            setRowModesModel,
          },
        }}
      />
    </div>
  );
};
